import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/ApiService';

const Challan = () => {
  const [challans, setChallans] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState({
    challanId: '',
    vehicleNumber: '',
    status: '',
    date: ''
  });

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpStep, setOtpStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const DOWNLOAD_PHONE = '9554494415';
  const itemsPerPage = 10;

  useEffect(() => {
    fetchChallans();
  }, [currentPage, searchParams]);

  const fetchChallans = async () => {
    setLoading(true);
    try {
      const queryParams = {
        page: currentPage,
        limit: itemsPerPage,
        challan_id: searchParams.challanId,
        vehicle_number: searchParams.vehicleNumber,
        status: searchParams.status,
        date: searchParams.date
      };

      const result = await apiService.get('/vehicles/admin/challans', queryParams);
      setChallans(result.challans || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch challans');
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = (challan) => {
    if (challan.status !== 'paid') {
      alert('This challan has not been marked as paid by the user.');
      return;
    }
    const parivahanUrl = `https://echallan.parivahan.gov.in/index/accused-challan?challan_no=${challan.challan_id}&vehicle_no=${challan.vehicle_number.replace(/\s/g, '')}`;
    window.open(parivahanUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRequestOtp = async () => {
    setIsOtpLoading(true);
    try {
      await apiService.post('/users/select-type', {
        user_type: 'admin',
        phone_number: DOWNLOAD_PHONE
      });
      setOtpStep(2);
      alert('OTP sent to ' + DOWNLOAD_PHONE);
    } catch (err) {
      alert(err.message || 'Failed to send OTP');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOtpAndDownload = async () => {
    if (otp.length !== 6) return alert('Enter 6-digit OTP');
    setIsOtpLoading(true);
    try {
      await apiService.post('/users/verify-otp', {
        phone_number: DOWNLOAD_PHONE,
        otp
      });
      setShowOtpModal(false);
      await performDownload();
    } catch (err) {
      alert(err.message || 'Invalid OTP');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const performDownload = async () => {
    setIsDownloading(true);
    try {
      const queryParams = {
        page: 1,
        limit: 10000,
        ...searchParams
      };
      const result = await apiService.get('/vehicles/admin/challans', queryParams);
      const allChallans = result.challans || [];

      const headers = ['Challan ID', 'Vehicle', 'Amount', 'Date', 'Status', 'Location'];
      const csvContent = [
        headers.join(','),
        ...allChallans.map(c => [
          c.challan_id,
          c.vehicle_number,
          c.amount,
          new Date(c.date).toLocaleDateString('en-GB'),
          c.status,
          c.location
        ].map(f => `"${f}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `challans_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="challan-page">
      <div className="page-header" style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Transaction & Challan Audit</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Analyze traffic violations and manage settlement statuses across the fleet.</p>
        </div>
        <button className="btn btn-secondary" onClick={() => setShowOtpModal(true)} disabled={isDownloading}>
          <i className="fas fa-file-export" style={{ marginRight: '8px' }}></i>
          {isDownloading ? 'Processing...' : 'Export Audit Log'}
        </button>
      </div>

      {/* Filters Hub */}
      <div className="admin-card" style={{ marginBottom: '32px', padding: '24px' }}>
        <div className="grid-responsive">
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>CHALLAN IDENTIFIER</label>
            <div style={{ position: 'relative' }}>
              <i className="fas fa-barcode" style={inputIconStyle}></i>
              <input type="text" name="challanId" value={searchParams.challanId} onChange={handleSearchChange} placeholder="TXN-000..." style={{ ...inputStyle, paddingLeft: '40px' }} />
            </div>
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>VEHICLE PLATE</label>
            <div style={{ position: 'relative' }}>
              <i className="fas fa-car" style={inputIconStyle}></i>
              <input type="text" name="vehicleNumber" value={searchParams.vehicleNumber} onChange={handleSearchChange} placeholder="MH12AB1234" style={{ ...inputStyle, paddingLeft: '40px' }} />
            </div>
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>SETTLEMENT STATUS</label>
            <select name="status" value={searchParams.status} onChange={handleSearchChange} style={inputStyle}>
              <option value="">Full Registry</option>
              <option value="paid">Settled / Paid</option>
              <option value="pending">Outstanding</option>
            </select>
          </div>
          <div className="col-3 col-lg-6 col-md-12">
            <label style={labelStyle}>AUDIT DATE</label>
            <input type="date" name="date" value={searchParams.date} onChange={handleSearchChange} style={inputStyle} />
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
          <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
        </div>
      ) : (
        <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-main)', textAlign: 'left' }}>
                  <th style={tableHeaderStyle}>CHALLAN ID</th>
                  <th style={tableHeaderStyle}>VEHICLE</th>
                  <th style={tableHeaderStyle}>USER</th>
                  <th style={tableHeaderStyle}>AMOUNT</th>
                  <th style={tableHeaderStyle}>FUNNEL</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>OPERATIONS</th>
                </tr>
              </thead>
              <tbody>
                {challans.map(c => (
                  <tr key={c.challan_id} className="table-row-hover" style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={tableCellStyle}>
                      <span style={{ fontWeight: '800', color: 'var(--text-main)', fontSize: '13px' }}>{c.challan_id}</span>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ display: 'inline-flex', padding: '6px 14px', background: 'var(--bg-main)', borderRadius: '8px', border: '1px solid var(--border-light)', fontSize: '12px', fontWeight: '800', letterSpacing: '0.5px', fontFamily: 'monospace' }}>
                        {c.vehicle_number}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      {c.checked_by ? (
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '13px', color: 'var(--text-main)' }}>{c.checked_by.name || 'User'}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{c.checked_by.phone_number}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px', fontWeight: '600' }}>Checked: {new Date(c.createdAt).toLocaleDateString('en-GB')}</div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-dim)', fontWeight: '800' }}>System / Admin</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px', fontWeight: '600' }}>Logged: {new Date(c.createdAt).toLocaleDateString('en-GB')}</div>
                        </div>
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{ fontWeight: '800', color: 'var(--primary)', fontSize: '15px' }}>₹{c.amount.toLocaleString()}</span>
                      <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>
                        {new Date(c.date).toLocaleDateString('en-GB')}
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      <span className={`status-badge ${c.funnel_stage === 'paid' ? 'status-completed' : c.funnel_stage === 'checkout_initiated' ? 'status-pending' : 'status-rejected'}`} style={{ fontSize: '10px', padding: '4px 12px' }}>
                        {(c.funnel_stage || 'discovered').toUpperCase().replace('_', ' ')}
                      </span>
                      {c.status === 'pending' && c.funnel_stage !== 'checkout_initiated' && (
                        <div style={{ fontSize: '10px', color: 'var(--text-dim)', marginTop: '4px', fontWeight: '700' }}>PENDING</div>
                      )}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      <button
                        onClick={() => handlePayNow(c)}
                        disabled={c.status !== 'paid'}
                        className="btn-action"
                        style={{
                          background: c.status === 'paid' ? 'var(--primary)' : 'var(--bg-main)',
                          color: c.status === 'paid' ? 'white' : 'var(--text-dim)',
                          opacity: c.status === 'paid' ? 1 : 0.5,
                          cursor: c.status === 'paid' ? 'pointer' : 'not-allowed',
                          padding: '8px 16px', borderRadius: '10px', fontSize: '12px', fontWeight: '800', border: 'none', transition: 'all 0.2s'
                        }}
                      >
                        <i className="fas fa-external-link-alt" style={{ marginRight: '6px' }}></i>
                        Resolve Now
                      </button>
                    </td>
                  </tr>
                ))}
                {challans.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ padding: '80px 20px', textAlign: 'center' }}>
                      <i className="fas fa-folder-open" style={{ fontSize: '40px', color: 'var(--border-light)', marginBottom: '16px', display: 'block' }}></i>
                      <div style={{ fontWeight: '700', color: 'var(--text-dim)' }}>No challan records discovered for this query.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ padding: '24px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-main)', borderTop: '1px solid var(--border-light)' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-dim)', fontWeight: '700' }}>DISPLAYING {challans.length} OF {total} ENTRIES</span>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn-nav"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
                style={navButtonStyle(currentPage === 1)}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="btn-nav"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                style={navButtonStyle(currentPage === totalPages)}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showOtpModal && (
        <div className="modal-overlay" style={{ backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-content admin-card" style={{ maxWidth: '420px', padding: '40px', textAlign: 'center', boxShadow: 'var(--shadow-lg)' }}>
            <div style={{ width: '64px', height: '64px', background: 'var(--primary-light)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '24px', margin: '0 auto 24px auto' }}>
              <i className="fas fa-user-shield"></i>
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '800', color: 'var(--text-main)' }}>Audit Authentication</h3>
            <p style={{ fontSize: '14px', color: 'var(--text-dim)', lineHeight: '1.6', marginBottom: '32px' }}>
              A 6-digit verification code is required to download sensitive transaction data.
            </p>

            {otpStep === 1 ? (
              <button className="btn btn-primary" style={{ width: '100%', padding: '16px' }} onClick={handleRequestOtp} disabled={isOtpLoading}>
                {isOtpLoading ? <i className="fas fa-spinner fa-spin"></i> : 'Dispatch Verification Code'}
              </button>
            ) : (
              <div>
                <input
                  type="text"
                  maxLength="6"
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  placeholder="------"
                  style={{
                    width: '100%', border: 'none', background: 'var(--bg-main)', padding: '20px', borderRadius: '16px',
                    fontSize: '32px', textAlign: 'center', letterSpacing: '12px', fontWeight: '800', color: 'var(--primary)', marginBottom: '24px',
                    outline: 'none', border: '2px solid var(--primary-light)'
                  }}
                />
                <button className="btn btn-primary" style={{ width: '100%', padding: '16px' }} onClick={handleVerifyOtpAndDownload} disabled={isOtpLoading}>
                  {isOtpLoading ? <i className="fas fa-spinner fa-spin"></i> : 'Confirm & Generate CSV'}
                </button>
              </div>
            )}
            <button className="btn" style={{ width: '100%', marginTop: '16px', border: 'none', background: 'transparent', color: 'var(--text-dim)', fontWeight: '700' }} onClick={() => { setShowOtpModal(false); setOtpStep(1); setOtp(''); }}>
              Cancel Operation
            </button>
          </div>
        </div>
      )}

      <style>{`
        .table-row-hover:hover { background: #fcfdfe !important; }
        .btn-nav:hover:not(:disabled) { background: white !important; color: var(--primary) !important; border-color: var(--primary) !important; transform: translateY(-2px); }
        .btn-action:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.1); box-shadow: var(--shadow-md); }
        .modal-overlay { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; z-index: 1000; }
      `}</style>
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '10px',
  fontWeight: '800',
  color: 'var(--text-dim)',
  letterSpacing: '1px',
  marginBottom: '8px'
};

const inputIconStyle = {
  position: 'absolute',
  left: '14px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-dim)',
  fontSize: '14px'
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '12px',
  border: '1px solid var(--border-light)',
  fontSize: '14px',
  fontWeight: '600',
  background: '#fcfcfc',
  transition: 'all 0.2s',
  outline: 'none',
  boxSizing: 'border-box'
};

const tableHeaderStyle = {
  padding: '16px 24px',
  color: 'var(--text-dim)',
  fontSize: '11px',
  fontWeight: '800',
  letterSpacing: '1px'
};

const tableCellStyle = {
  padding: '20px 24px',
  fontSize: '14px'
};

const navButtonStyle = (disabled) => ({
  width: '40px', height: '40px', borderRadius: '12px', border: '1px solid var(--border-light)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
  background: 'white', color: disabled ? 'var(--border-light)' : 'var(--text-main)',
  transition: 'all 0.2s', boxShadow: 'var(--shadow-sm)'
});

export default Challan;
